import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

const stepMeta = [
  {
    num: '01',
    title: '申请大额成功',
    desc: '您的大额申请已提交并审核通过',
    icon: '/images/progress_shenqing.png',
  },
  {
    num: '02',
    title: '正在录入系统',
    desc: '相关资料已通过，正在录入系统',
    icon: '/images/progress_luru.png',
  },
  {
    num: '03',
    title: '完成免税打款',
    desc: '免税审核已完成，已进入打款流程',
    icon: '/images/progress_shui.png',
    actionLabel: '查看免税证明',
    actionBg: '/images/progress_mianshui.png',
  },
  {
    num: '04',
    title: '正在准备大额拨款',
    desc: '已完成所有前置流程，正在准备大额拨款',
    icon: '/images/progress_bokuan.png',
    actionLabel: '查看打款回单',
    actionBg: '/images/progress_huidan.png',
  },
  {
    num: '05',
    title: '打款成功',
    desc: '预计近期完成打款，请耐心等待',
    icon: '/images/progress_dakuan.png',
  },
];

function getCompletedSteps(progress: number): number {
  if (progress >= 100) return 5;
  if (progress >= 80) return 4;
  if (progress >= 60) return 3;
  if (progress >= 40) return 2;
  if (progress >= 20) return 1;
  return 0;
}

function formatDateTime(dateStr: string) {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

export default function RefundProgress() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(88);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptImage, setReceiptImage] = useState('');
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  useEffect(() => {
    api.get('/investment-refund/progress').then(({ data }) => {
      setProgress(data.progress ?? 88);
      setCurrentStep(data.current_step ?? null);
      setMessage(data.message || '');
      setCreatedAt(data.created_at || '');
    }).catch(() => {
      navigate('/investment-refund', { replace: true });
    }).finally(() => setLoading(false));
  }, [navigate]);

  const completedSteps = currentStep ?? getCompletedSteps(progress);
  const needleRotation = -140 + 1.8 * progress;

  async function handleViewReceipt(type: 'tax' | 'payment') {
    if (loadingReceipt) return;
    setLoadingReceipt(true);
    const endpoint = type === 'payment' ? '/investment-refund/payment-receipt' : '/investment-refund/receipt';
    try {
      const { data } = await api.get(endpoint);
      setReceiptImage(data.image);
      setShowReceipt(true);
    } catch {
      toast.error('获取凭证失败');
    } finally {
      setLoadingReceipt(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex items-center justify-center" style={{ backgroundColor: '#c22a1e' }}>
        <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="max-w-md mx-auto min-h-screen pb-8 relative"
      style={{
        backgroundImage: 'url(/images/progress_jindu.png)',
        backgroundSize: '100% auto',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        backgroundColor: '#c22a1e',
      }}
    >
      <AuthGuard />

      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      {/* Gauge area */}
      <div className="relative" style={{ paddingTop: '350px' }}>
        {/* Needle overlay */}
        <div
          className="absolute"
          style={{
            left: '50%',
            top: '310px',
            width: 120,
            height: 100,
          }}
        >
          <img
            src="/images/progress_zhinanzhen.png"
            alt=""
            className="absolute"
            style={{
              width: '255px',
              transformOrigin: '12% 78%',
              transform: `translate(-12%, -78%) rotate(${needleRotation}deg)`,
            }}
          />
        </div>

        {/* Percentage text */}
        <div className="absolute inset-x-0" style={{ top: '230px' }}>
          <div className="text-center">
            <div className="text-[#555] text-xs mb-0.5">目前进度</div>
            <div className="text-[#c4161c] text-3xl">{progress}%</div>
          </div>
        </div>
      </div>

      {/* Message banner */}
      {message && (
        <div className="px-3 mt-2 mb-4">
          <div
            className="rounded-xl py-3 px-4 text-center"
            style={{
              backgroundImage: 'url(/images/progress_dae.png)',
              backgroundSize: '100% 100%',
            }}
          >
            {message.split('\n').map((line, i) => (
              <div key={i} className="text-[#ffd700] text-lg font-bold leading-relaxed">
                {line}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Steps card */}
      <div className="px-3">
        <div
          className="rounded-2xl p-4 pt-5 bg-white/70" 
        >
          {stepMeta.map((step, idx) => {
            const isActive = idx < completedSteps;
            const isLast = idx === stepMeta.length - 1;

            return (
              <div key={step.num} className="flex items-start">
                {/* Timeline column */}
                <div className="flex flex-col items-center w-[30px] flex-shrink-0 ">
                  <span className="relative mt-[20px]">
                    <span className={`absolute insert top-[5px] left-[5px] text-[11px] font-bold ${isActive ? 'text-[#fff]' : 'text-gray-400'}`}>
                      {step.num}
                    </span>
                    <img
                      src={isActive ? '/images/progress_yi.png' : '/images/progress_huiyuan.png'}
                      alt=""
                      className="w-[24px] h-[24px] my-0.5 flex-shrink-0"
                    />
                  </span>
                </div>

                {/* Step icon */}
                <div className="flex-shrink-0 pt-1 relative z-1">
                  <img
                    src={step.icon}
                    alt=""
                    className={`w-[60px] h-[60px] ${!isActive ? 'opacity-40 grayscale' : ''}`}
                  />
                  {!isLast && (
                    <div className={`ml-[28px] w-[5px] flex-1 min-h-[20px] ${isActive ? 'bg-[#c4161c]' : 'bg-gray-300'}`} />
                  )}
                </div>

                {/* Step content card */}
                <div
                  className="flex-1 rounded-xl pl-5 pr-3 py-1 -ml-2"
                  style={{
                    backgroundImage: `url(${isActive ? '/images/progress_fendi.png' : '/images/progress_huidi.png'})`,
                    backgroundSize: '100% 100%',
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={`text-[12px] font-bold ${isActive ? 'text-[#c4161c]' : 'text-gray-400'}`}>
                      {step.title}
                    </h4>
                    {step.actionLabel && isActive && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewReceipt(idx === 3 ? 'payment' : 'tax'); }}
                        disabled={loadingReceipt}
                        className="flex-shrink-0 h-4 px-3 rounded-full text-white text-[9px] font-medium flex items-center justify-center"
                        style={{
                          backgroundImage: `url(${step.actionBg})`,
                          backgroundSize: '100% 100%',
                        }}
                      >
                        {step.actionLabel}
                      </button>
                    )}
                  </div>
                  <p className={`text-[11px] mt-1 ${isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                    {step.desc}
                  </p>
                  {createdAt && (
                    <p className={`text-[10px] mt-1 ${isActive ? 'text-gray-400' : 'text-gray-300'}`}>
                      {formatDateTime(createdAt)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Receipt modal */}
      {showReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowReceipt(false)}>
          <div className="relative max-w-[95vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="absolute -top-3 -right-3 flex gap-2">
              <a
                href={receiptImage}
                download="凭证.png"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-600 shadow-lg"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </a>
              <button
                onClick={() => setShowReceipt(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-600 shadow-lg text-lg font-bold"
              >
                &times;
              </button>
            </div>
            <img src={receiptImage} alt="凭证" className="max-w-full max-h-[85vh] rounded-lg shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
